import type { ReactNode } from 'react';
import { useRef } from 'react';
import { createPortal } from 'react-dom';

import closeIcon from '@/assets/icons/close.svg';
import successLogoIcon from '@/assets/icons/success-logo.svg';
import { HubModalWarningHeader } from '@/shared/components/HubModalWarningHeader';
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock';
import { useEscapeKey } from '@/shared/hooks/useEscapeKey';
import { cn } from '@/shared/lib/cn';
import { iconMaskStyle } from '@/shared/lib/icon-mask';
import {
  OUTLINE_PILL_BUTTON_CLASS,
  PRIMARY_ACTION_BUTTON_BASE_CLASS,
} from '@/shared/lib/primary-action-button-classes';

export type ConfirmDialogTone = 'warning' | 'danger' | 'success';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  hint?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  confirmDisabled?: boolean;
  /**
   * Retained for future visual differentiation; today both tones share the
   * warm-orange treatment from the Figma spec.
   */
  tone?: ConfirmDialogTone;
  cancelIcon?: ReactNode;
  confirmIcon?: ReactNode;
  /** Allow popovers/dropdowns inside the dialog to overflow without clipping. */
  allowOverflow?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const SUCCESS_HEADER_STYLES = {
  headerBg: 'bg-[rgba(150,222,174,0.2)]',
  ring: 'bg-[rgba(40,167,69,0.05)] shadow-[0_5px_4px_rgba(40,167,69,0.2)]',
  border: 'border-[rgba(40,167,69,0.1)]',
  iconBg: 'bg-transparent',
  iconSrc: successLogoIcon,
} as const;

export function ConfirmDialog({
  open,
  title,
  description,
  hint,
  confirmLabel,
  cancelLabel,
  busy = false,
  confirmDisabled = false,
  tone = 'warning',
  cancelIcon,
  confirmIcon,
  allowOverflow = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const isSuccessTone = tone === 'success';

  useEscapeKey(onCancel, open && !busy);
  useBodyScrollLock(open);

  if (!open) {
    return null;
  }

  /** Portals to `document.body` so the overlay sits above app chrome (e.g. sidebar `z-50`) — the header uses `z-20` and would otherwise cap stacking. */
  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6"
      onMouseDown={(e) => {
        if (!busy && e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div aria-hidden className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        tabIndex={-1}
        className={`relative z-10 flex w-full max-w-[480px] flex-col items-center rounded-2xl bg-white shadow-modal focus:outline-none ${allowOverflow ? 'overflow-visible' : 'overflow-hidden'}`}
      >
        {isSuccessTone ? (
          <div className={`flex w-full flex-col items-center gap-2 py-4 ${SUCCESS_HEADER_STYLES.headerBg}`}>
            <div className="relative flex items-center justify-center">
              <div
                className={`flex size-[100px] items-center justify-center rounded-full ${SUCCESS_HEADER_STYLES.ring}`}
              >
                <div className="flex items-center justify-center rounded-full">
                  <div
                    className={`flex size-[72px] items-center justify-center overflow-hidden rounded-full ${SUCCESS_HEADER_STYLES.iconBg}`}
                  >
                    <img
                      src={SUCCESS_HEADER_STYLES.iconSrc}
                      alt=""
                      aria-hidden
                      className="size-[72px] shrink-0"
                    />
                  </div>
                </div>
              </div>
            </div>
            <h2
              id="confirm-dialog-title"
              className="px-6 text-center text-xl font-semibold leading-normal text-ink"
            >
              {title}
            </h2>
          </div>
        ) : (
          <HubModalWarningHeader title={title} titleId="confirm-dialog-title" />
        )}

        <div className="flex w-full flex-col gap-6 p-6">
          {description ? (
            <div className="text-center text-sm leading-normal text-ink">{description}</div>
          ) : null}
          {hint ? (
            <div className="text-center text-[13px] leading-normal text-ink-muted/70">{hint}</div>
          ) : null}

          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className={cn(OUTLINE_PILL_BUTTON_CLASS, 'border-ink-muted/20')}
            >
              <span>{cancelLabel}</span>
              {cancelIcon ?? (
                <span aria-hidden className="block size-[10px]" style={iconMaskStyle(closeIcon)} />
              )}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy || confirmDisabled}
              aria-busy={busy || undefined}
              className={PRIMARY_ACTION_BUTTON_BASE_CLASS}
            >
              <span>{confirmLabel}</span>
              {confirmIcon ?? null}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
